<template>
  <div class="theme-switch" :class="`theme-switch--${variant}`">
    <button
      ref="triggerRef"
      type="button"
      class="theme-switch__trigger"
      :aria-expanded="isOpen"
      :aria-controls="panelId"
      :aria-label="variant === 'header' ? `Тема оформления: ${currentOption.label}` : undefined"
      @click="toggle"
    >
      <component :is="currentOption.icon" filled aria-hidden="true"/>
      <span v-if="variant === 'nav'">Тема</span>
    </button>
    <Teleport to="body">
      <Transition name="theme-switch-panel">
        <div
          v-if="isOpen"
          :id="panelId"
          ref="panelRef"
          class="theme-switch__panel"
          :class="`theme-switch__panel--${placement}`"
          :style="panelStyle"
          @keydown.esc.stop="close(true)"
          @focusout="onFocusOut"
        >
          <fieldset class="theme-switch__fieldset">
            <legend class="theme-switch__legend">Тема оформления</legend>
            <label
              v-for="option in options"
              :key="option.value"
              class="theme-switch__option"
              :class="{'theme-switch__option--active': option.value === preference}"
              @click="onOptionClick"
            >
              <input
                v-model="preference"
                class="theme-switch__input"
                type="radio"
                :name="panelId"
                :value="option.value"
                @keydown.enter.prevent="close(true)"
              >
              <component :is="option.icon" filled aria-hidden="true"/>
              <span>{{ option.label }}</span>
            </label>
          </fieldset>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import type {Component} from 'vue';
import type {ColorThemePreference} from '~/composables/useColorTheme';
import {useUniqueId} from '~/composables/useUniqueId';
import SvgoThemeSystem from '~/assets/icons/theme-system.svg?componentext';
import SvgoThemeLight from '~/assets/icons/theme-light.svg?componentext';
import SvgoThemeDark from '~/assets/icons/theme-dark.svg?componentext';

const props = withDefaults(defineProps<{
  variant?: 'header' | 'nav';
}>(), {
  variant: 'header',
});

const PANEL_GAP = 8;
const VIEWPORT_MARGIN = 12;

const options: {value: ColorThemePreference, label: string, icon: Component}[] = [
  {value: 'system', label: 'Как в системе', icon: SvgoThemeSystem},
  {value: 'light', label: 'Светлая', icon: SvgoThemeLight},
  {value: 'dark', label: 'Тёмная', icon: SvgoThemeDark},
];

const {preference} = useColorTheme();
const panelId = useUniqueId('theme-switch');
const isOpen = ref(false);
const triggerRef = ref<HTMLButtonElement | null>(null);
const panelRef = ref<HTMLElement | null>(null);
const placement = ref<'below' | 'above'>('below');
const panelStyle = ref<Record<string, string>>({});

const currentOption = computed(() => options.find((option) => option.value === preference.value) ?? options[0]);

function updatePosition() {
  const trigger = triggerRef.value;
  if (!trigger) {
    return;
  }

  const rect = trigger.getBoundingClientRect();
  const panelHeight = panelRef.value?.offsetHeight ?? 160;
  const spaceBelow = window.innerHeight - rect.bottom;
  placement.value = props.variant === 'nav' || spaceBelow < panelHeight + PANEL_GAP + VIEWPORT_MARGIN ? 'above' : 'below';

  const right = Math.max(VIEWPORT_MARGIN, window.innerWidth - rect.right);
  panelStyle.value = placement.value === 'below'
    ? {top: `${rect.bottom + PANEL_GAP}px`, right: `${right}px`}
    : {bottom: `${window.innerHeight - rect.top + PANEL_GAP}px`, right: `${right}px`};
}

async function open() {
  isOpen.value = true;
  updatePosition();
  await nextTick();
  updatePosition();
  panelRef.value?.querySelector<HTMLInputElement>('input:checked')?.focus();
}

function close(restoreFocus = false) {
  if (!isOpen.value) {
    return;
  }
  isOpen.value = false;
  if (restoreFocus) {
    triggerRef.value?.focus();
  }
}

function toggle() {
  if (isOpen.value) {
    close();
  } else {
    open();
  }
}

function onOptionClick(event: MouseEvent) {
  if (event.detail > 0) {
    close(true);
  }
}

function onFocusOut(event: FocusEvent) {
  const next = event.relatedTarget as Node | null;
  if (next && !panelRef.value?.contains(next) && !triggerRef.value?.contains(next)) {
    close();
  }
}

function onDocumentPointerDown(event: PointerEvent) {
  const target = event.target as Node;
  if (!panelRef.value?.contains(target) && !triggerRef.value?.contains(target)) {
    close();
  }
}

function onViewportChange() {
  if (isOpen.value) {
    updatePosition();
  }
}

onMounted(() => {
  document.addEventListener('pointerdown', onDocumentPointerDown);
  window.addEventListener('resize', onViewportChange);
  window.addEventListener('scroll', onViewportChange, {passive: true});
});

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onDocumentPointerDown);
  window.removeEventListener('resize', onViewportChange);
  window.removeEventListener('scroll', onViewportChange);
});
</script>

<style lang="scss" scoped>
.theme-switch {
  line-height: 0;

  &__trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    background: none;
    color: $color-black;
    font: inherit;
    cursor: pointer;
    transition: color 0.18s ease-out, border-color 0.18s ease-out, box-shadow 0.18s ease-out;

    :deep(svg) {
      width: 20px;
      height: 20px;
      margin: 0;
      fill: none;
    }

    &:focus-visible {
      outline: 2px solid $color-purple;
      outline-offset: 2px;
    }
  }

  &--header &__trigger {
    width: 40px;
    height: 40px;
    border: 1px solid $color-light-gray;
    border-radius: 50%;
    background: $color-white;

    &:hover,
    &[aria-expanded='true'] {
      color: $color-accent-text;
      border-color: $color-purple;
      box-shadow: 0 0 0 4px alpha($color-purple, 0.12);
    }

    @include media(header-nav) {
      width: 24px;
      height: 24px;
      border: none;
      border-radius: 6px;
      background: none;
      color: $color-accent-text;

      :deep(svg) {
        width: 24px;
        height: 24px;
      }

      &:hover,
      &[aria-expanded='true'] {
        border-color: transparent;
        box-shadow: none;
        color: $color-accent-text-hover;
      }
    }
  }

  &--nav &__trigger {
    flex-direction: column;
    gap: 4px;
    width: 100%;
    color: inherit;
    font-size: inherit;
    line-height: inherit;

    &[aria-expanded='true'] {
      color: $color-accent-text;
    }
  }

  &__panel {
    position: fixed;
    z-index: 1000;
    min-width: 204px;
    padding: 6px;
    border-radius: 16px;
    background: $color-white;
    box-shadow: $shadow3-box-shadow;
    line-height: 1.2;

    &--below {
      transform-origin: top right;
    }

    &--above {
      transform-origin: bottom right;
    }
  }

  &__fieldset {
    margin: 0;
    padding: 0;
    border: none;
    min-inline-size: 0;
  }

  &__legend {
    padding: 6px 12px 8px;
    color: $color-gray;
    font-size: 12px;
  }

  &__option {
    position: relative;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 10px;
    color: $color-black;
    font-size: 14px;
    cursor: pointer;
    transition: background-color 0.15s ease-out, color 0.15s ease-out;

    :deep(svg) {
      flex-shrink: 0;
      width: 18px;
      height: 18px;
      margin: 0;
      fill: none;
      color: $color-gray;
    }

    &:hover {
      background: $color-dark-white;
    }

    &:has(:focus-visible) {
      outline: 2px solid $color-purple;
      outline-offset: -2px;
    }

    &--active {
      color: $color-accent-text;
      background: $color-pink;

      :deep(svg) {
        color: $color-accent-text;
      }

      &:hover {
        background: $color-pink;
      }
    }
  }

  &__input {
    position: absolute;
    inset: 0;
    margin: 0;
    opacity: 0;
    cursor: pointer;
  }
}

.theme-switch-panel-enter-active {
  transition: opacity 0.18s ease-out, transform 0.18s ease-out;
}

.theme-switch-panel-leave-active {
  transition: opacity 0.14s ease-in, transform 0.14s ease-in;
}

.theme-switch-panel-enter-from,
.theme-switch-panel-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.97);
}

@media (prefers-reduced-motion: reduce) {
  .theme-switch__trigger,
  .theme-switch__option,
  .theme-switch-panel-enter-active,
  .theme-switch-panel-leave-active {
    transition: none;
  }
}
</style>
